import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  api,
  ErroApi,
  mensagemDoErro,
  type MarcaPublica,
  type ProvedorSessao,
  type UsuarioSessao,
} from './api';

type TipoAviso = 'sucesso' | 'erro' | 'info';

interface AvisoAtivo {
  id: string;
  tipo: TipoAviso;
  texto: string;
}

interface Contexto {
  carregando: boolean;
  usuario: UsuarioSessao | null;
  provedor: ProvedorSessao | null;
  marca: MarcaPublica | null;
  entrar: (email: string, senha: string) => Promise<void>;
  sair: () => Promise<void>;
  notificar: (tipo: TipoAviso, texto: string) => void;
}

const ContextoApp = createContext<Contexto | null>(null);

const ICONES_TOAST: Record<TipoAviso, string> = {
  sucesso: '✓',
  erro: '✕',
  info: 'ℹ',
};

export function ProvedorApp({ children }: { children: ReactNode }) {
  const [carregando, setCarregando] = useState(true);
  const [usuario, setUsuario] = useState<UsuarioSessao | null>(null);
  const [provedor, setProvedor] = useState<ProvedorSessao | null>(null);
  const [marca, setMarca] = useState<MarcaPublica | null>(null);
  const [avisos, setAvisos] = useState<AvisoAtivo[]>([]);
  const contador = useRef(0);

  const notificar = useCallback((tipo: TipoAviso, texto: string) => {
    contador.current += 1;
    const id = `aviso-${contador.current}`;
    setAvisos((atual) => [...atual, { id, tipo, texto }]);
    window.setTimeout(() => {
      setAvisos((atual) => atual.filter((a) => a.id !== id));
    }, 7000);
  }, []);

  const dispensar = useCallback((id: string) => {
    setAvisos((atual) => atual.filter((a) => a.id !== id));
  }, []);

  useEffect(() => {
    let cancelado = false;

    const iniciar = async () => {
      const [marcaPublica, sessao] = await Promise.all([
        api.marca().catch(() => null),
        api.sessao().catch((erro: unknown) => {
          if (erro instanceof ErroApi && (erro.status === 401 || erro.status === 404)) return null;
          return null;
        }),
      ]);
      if (cancelado) return;
      if (marcaPublica) setMarca(marcaPublica);
      if (sessao) {
        setUsuario(sessao.usuario);
        setProvedor(sessao.provedor);
      }
      setCarregando(false);
    };

    void iniciar();
    return () => {
      cancelado = true;
    };
  }, []);

  const entrar = useCallback(async (email: string, senha: string) => {
    await api.entrar(email, senha);
    const sessao = await api.sessao();
    setUsuario(sessao.usuario);
    setProvedor(sessao.provedor);
  }, []);

  const sair = useCallback(async () => {
    try {
      await api.sair();
    } finally {
      setUsuario(null);
      setProvedor(null);
    }
  }, []);

  const valor = useMemo<Contexto>(
    () => ({ carregando, usuario, provedor, marca, entrar, sair, notificar }),
    [carregando, usuario, provedor, marca, entrar, sair, notificar],
  );

  return (
    <ContextoApp.Provider value={valor}>
      {children}
      <div className="toasts" aria-live="polite" aria-atomic="false">
        {avisos.map((aviso) => (
          <div
            key={aviso.id}
            className={`toast toast--${aviso.tipo}`}
            role={aviso.tipo === 'erro' ? 'alert' : 'status'}
          >
            <span aria-hidden="true">{ICONES_TOAST[aviso.tipo]}</span>
            <span>{aviso.texto}</span>
            <button
              type="button"
              className="toast__fechar"
              onClick={() => dispensar(aviso.id)}
              aria-label="Fechar aviso"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </ContextoApp.Provider>
  );
}

export function useApp(): Contexto {
  const contexto = useContext(ContextoApp);
  if (!contexto) throw new Error('useApp precisa estar dentro de <ProvedorApp>.');
  return contexto;
}

export { mensagemDoErro };
